import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTickets } from '../../services/ticketsApi';
import { segui, giro } from '../../services/flussoGiocate';
import { chiSono } from '../Schedina/SchedinaCollegata';
import { daLampeggiare, segnaRipropostaVista } from '../../utils/riproposteViste';
import { restante, useAdesso } from '../../utils/contoAllaRovescia';

/**
 * LA SPIA IN ALTO: «ti hanno riproposto», su OGNI pagina.
 *
 * Chi sta componendo una schedina vede la riga sotto la schedina; chi e' in
 * lista scommesse vede la riga che lampeggia li'. Ma chi sta in Home, nel
 * casino' o nei movimenti non vede niente -- e una riproposta che scade in
 * trentotto secondi non puo' aspettare che torni per caso. Questa e' la
 * terza forma della stessa cosa (tempo-reale.md: avviso altrove / riga nella
 * lista / striscia sotto la betslip): una spia sola, tre posti.
 *
 * COSA FA: dice quante riproposte aspettano una risposta e il tempo di quella
 * che scade prima; lampeggia finche' non la si apre; toccata, porta al ticket
 * in lista scommesse. Sparisce da sola quando non c'e' piu' niente da
 * rispondere.
 *
 * COSA NON FA: non e' una finestra che copre la pagina e non suona -- e' una
 * spia, non un allarme. Non si risponde da qui. La vede chi gioca e chi
 * compila per altri (giocatore, agenzia): per gli altri livelli la schedina
 * non e' uno strumento.
 *
 * COME SI AGGIORNA, oggi: chiede le riproposte aperte ogni 20 secondi a scheda
 * visibile, e subito dopo una giocata o una risposta (evento `giocataCambiata`).
 * Col flusso del giocatore (spinta GIOCATA_CAMBIATA) il giro sparisce.
 */
export default function SpiaRiproposte({ dentro, navigate }) {
  const { t } = useTranslation();
  const chi = dentro ? chiSono() : 'ospite';
  const mostra = chi === 'giocatore' || chi === 'agenzia';

  const [righe, setRighe] = useState([]);
  const [, ridisegna] = useState(0);

  const ricarica = useCallback(async () => {
    if (!mostra) return;
    try {
      /* Si chiedono ANCHE le giocate in accettazione, non per mostrarle:
         la spia sta su ogni pagina, ed e' lei che tiene aperto il flusso
         delle giocate che aspettano quando la striscia e la lista non ci
         sono. Una riproposta nasce su una giocata in accettazione: senza il
         flusso su quella, la spia la scoprirebbe al giro dopo. */
      const d = await getTickets({ page: 1, limit: 10, stati: ['DA_ACCETTARE', 'RIPROPOSTA'] });
      segui('spia', d.tickets || []);
      setRighe((d.tickets || []).filter((g) => g.status === 'RIPROPOSTA'));
    } catch (e) {
      /* La spia che non si aggiorna non deve portarsi dietro la barra:
         resta com'era, e al giro dopo riprova. */
    }
  }, [mostra]);

  useEffect(() => {
    if (!mostra) { setRighe([]); return undefined; }
    ricarica();
    /* Lento (20 s) col flusso vivo, veloce (5 s) se una giocata aspetta
       senza flusso: vedi services/flussoGiocate.js. */
    const ferma = giro(ricarica, { veloce: 5000, lento: 20000 });
    const alRitorno = () => { if (!document.hidden) ricarica(); };
    const alCambio = () => ricarica();
    const allaVista = () => ridisegna((n) => n + 1);
    document.addEventListener('visibilitychange', alRitorno);
    window.addEventListener('giocataCambiata', alCambio);
    window.addEventListener('ripropostaVista', allaVista);
    return () => {
      ferma();
      document.removeEventListener('visibilitychange', alRitorno);
      window.removeEventListener('giocataCambiata', alCambio);
      window.removeEventListener('ripropostaVista', allaVista);
      segui('spia', []);
    };
  }, [mostra, ricarica]);

  const adesso = useAdesso(righe.length > 0);

  /* Quelle gia' scadute spariscono da sole, senza aspettare il giro. */
  const vive = righe.filter((g) => {
    const fine = g.riproposta && g.riproposta.scadeIl ? new Date(g.riproposta.scadeIl).getTime() : NaN;
    return Number.isNaN(fine) || fine > adesso;
  });
  if (!mostra || vive.length === 0) return null;

  /* Quella che scade prima comanda il tempo e il tocco. */
  const prima = vive.reduce((a, b) => {
    const fa = a.riproposta && a.riproposta.scadeIl ? new Date(a.riproposta.scadeIl).getTime() : Infinity;
    const fb = b.riproposta && b.riproposta.scadeIl ? new Date(b.riproposta.scadeIl).getTime() : Infinity;
    return fb < fa ? b : a;
  });
  const lampeggia = vive.some(daLampeggiare);
  const testo = vive.length === 1 ? t('Ti hanno riproposto') : t('{{count}} da rispondere', { count: vive.length });
  const tempo = restante(prima.riproposta && prima.riproposta.scadeIl, adesso);

  return (
    <button
      type="button"
      className={'spia-riproposta' + (lampeggia ? ' lvc-lampeggia' : '')}
      aria-label={`${testo}, ${tempo || t('tempo non noto')}`}
      onClick={() => {
        segnaRipropostaVista(prima.ticketCode);
        navigate(`/my-account/lista-scommesse?codice=${encodeURIComponent(prima.ticketCode || '')}`);
      }}
    >
      <span className={'spia-pallino' + (lampeggia ? ' lvc-lampeggia-pallino' : '')} />
      <span className="spia-testo">{testo}</span>
      {tempo ? <span className="spia-tempo">{tempo}</span> : null}
    </button>
  );
}
